import { Controller } from "@hotwired/stimulus";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { createRoot } from "react-dom/client";
import { useState, useEffect } from "react";
import axios from "axios";

const reactSelectStyles = {
  control: (state) =>
    `block w-full rounded-lg bg-white px-3.5 text-base !text-gray-900 !cursor-pointer
           placeholder:!text-gray-400 outline-none ring-1 !shadow-none !border-0 py-1
           ${state.isFocused ? "ring-2 !ring-default-600" : "!ring-gray-300"}
           ${state.isError ? "!ring-red-500" : ""}`,
  placeholder: () => "text-base !text-gray-400",
  input: () =>
    "text-base !text-gray-900 focus:!shadow-none focus:!ring-0 [&>input]:!ring-0",
  inputContainer: () =>
    "!shadow-none !ring-0 !outline-none [&>input]:focus:!shadow-none [&>input]:focus:!ring-0",
  singleValue: () => "text-base !text-gray-900",
  valueContainer: () => "!p-0",
  multiValue: () =>
    "!rounded bg-white !bg-default-50 border !border-default-200 !text-default-600 overflow-hidden flex items-center text-base font-medium px-1",
  multiValueLabel: () => "text-base font-medium !text-default-600 ",
  multiValueRemove: (state) =>
    `p-0 rounded-full h-5 w-5 flex items-center justify-center mr-1 border-0
           ${state.isFocused ? "!bg-default-600 text-white" : "!bg-default-50 !text-default-600"}`,
  menu: () => "mt-1 overflow-hidden bg-white rounded-md shadow-lg",
  menuList: () => "text-base !text-gray-900 max-h-60",
  option: (state) =>
    `px-3.5 py-2 !cursor-pointer
           ${state.isSelected ? "!bg-default-50 !text-default-600" : ""}
           ${state.isFocused ? "!bg-default-50 !text-default-600" : ""}`,
  indicatorsContainer: () =>
    "!bg-default-50 [&_svg]:!text-default-600 !cursor-pointer rounded-full !h-[30px]",
  indicatorSeparator: () => "!bg-transparent",
};

interface OptionInterface {
  label: string;
  value: string;
}

interface GroupedOption {
  readonly label: string;
  readonly options: OptionInterface[];
}

type SelectedValue = string | number;

const findSelectedOptions = (
  options: (OptionInterface | GroupedOption)[],
  ids: SelectedValue[],
) => {
  const selected: OptionInterface[] = [];

  ids.forEach((id) => {
    const flatOption = options.find(
      (opt) => "value" in opt && String(opt.value) === String(id),
    ) as OptionInterface;

    if (flatOption) {
      selected.push(flatOption);
      return;
    }

    // Check in grouped options
    const groupedOption = options
      .filter((opt): opt is GroupedOption => "options" in opt)
      .flatMap((group) => group.options)
      .find((opt) => String(opt.value) === String(id));

    if (groupedOption) {
      selected.push(groupedOption);
    }
  });

  return selected;
};

export function MultiSelectComponent({
  selectedIds = [],
  options = [],
  name,
  autocompleteUrl,
  labelPath,
  valuePath = "id",
  isMulti = true,
  creatable = false,
  isClearable = false,
}: {
  selectedIds: SelectedValue[];
  options?: OptionInterface[];
  name: string;
  autocompleteUrl?: string;
  labelPath?: string;
  valuePath?: string;
  idPath?: string;
  isMulti?: boolean;
  creatable?: boolean;
  isClearable?: boolean;
}) {
  const safeMulti = Boolean(isMulti);
  const props = {}; // { isFocused: true, defaultMenuIsOpen: true, menuIsOpen: true };
  const [inputValues, setInputValues] = useState(selectedIds.map(String));
  const [localOptions, setLocalOptions] = useState(
    options.map((opt) => ({ ...opt, value: String(opt.value) })),
  );
  const selectedOptions = findSelectedOptions(localOptions, inputValues);

  const formattedOptions = (data = []) =>
    data.map((node) => {
      let h = {};
      if (labelPath) {
        h["label"] = node[labelPath];
      }
      if (valuePath) {
        h["value"] = String(node[valuePath]);
      }
      return h;
    });

  const autocompleteOptionsQuery = (inputValue: string) => {
    return new Promise<OptionInterface[]>(async (resolve) => {
      const url = new URL(autocompleteUrl, window.location.origin);

      if (String(inputValue).length >= 1) {
        url.searchParams.set("q", inputValue);
      } else if (!isClearable && selectedIds.length > 0) {
        // Only preload by ids when NOT in clearable mode.
        // In clearable mode, empty input fetches all recent items
        // so the user can browse and pick a different one.
        selectedIds.forEach((id) => {
          url.searchParams.append("ids[]", id);
        });
      }

      try {
        const { data = [] } = await axios({
          method: "get",
          url: url.toString(),
          headers: {
            Accept: "application/json",
          },
        });

        const formatted = formattedOptions(data);
        setLocalOptions((prev) => {
          // Preserve currently selected options not present in new results
          const newValues = new Set(formatted.map((o) => o.value));
          const kept = prev.filter(
            (o) => inputValues.includes(o.value) && !newValues.has(o.value),
          );
          return [...kept, ...formatted];
        });
        resolve(formatted);
      } catch (error) {
        console.error("Error fetching options:", error);
        resolve([]);
      }
    });
  };

  const RenderedComponent = !!creatable
    ? CreatableSelect
    : !!autocompleteUrl
      ? AsyncSelect
      : Select;

  const componentProps = !!autocompleteUrl
    ? {
        cacheOptions: !isClearable,
        loadOptions: autocompleteOptionsQuery,
        defaultOptions: localOptions.length > 0 ? localOptions : true,
      }
    : { options: localOptions };

  useEffect(() => {
    const uniqueId = `select-portal-${autocompleteUrl}`;
    if (!document.getElementById(uniqueId)) {
      const portalContainer = document.createElement("div");
      portalContainer.id = uniqueId;
      document.body.appendChild(portalContainer);
    }
  }, [autocompleteUrl]);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  return (
    <>
      <RenderedComponent
        isMulti={safeMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!safeMulti}
        {...props}
        {...componentProps}
        defaultValue={selectedOptions}
        value={selectedOptions}
        menuPlacement="auto"
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        placeholder="Sélectionner..."
        components={{
          Menu: (props) => {
            return (
              <components.Menu {...props}>
                {props.children}
                {safeMulti && (
                  <div
                    className="px-3 py-2 border-t border-gray-200 bg-default-50"
                    style={{
                      position: "sticky",
                      bottom: 0,
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuIsOpen(false);
                      }}
                      className="w-full bg-default-600 text-white px-3.5 py-2 rounded-md text-sm font-medium hover:bg-default-700 transition-colors"
                    >
                      Confirmer
                    </button>
                  </div>
                )}
              </components.Menu>
            );
          },
        }}
        noOptionsMessage={({ inputValue }) => {
          if (inputValue.length >= 1) {
            return `Aucun résultat pour '${inputValue}'`;
          } else {
            return "Rechercher...";
          }
        }}
        onChange={(currentSelection) => {
          // For multi-select
          if (isMulti) {
            const newSelection = Array.isArray(currentSelection)
              ? currentSelection
              : [currentSelection];

            // Check for any new options not in localOptions and add them
            newSelection.forEach((option) => {
              const exists = localOptions.some(
                (opt) => opt.value === String(option.value),
              );
              if (!exists) {
                setLocalOptions((prev) => [...prev, option]);
              }
            });

            // Update input values
            setInputValues(newSelection.map((opt) => String(opt.value)));
          }
          // For single select
          else {
            if (currentSelection) {
              // Check if this is a new option
              const exists = localOptions.some(
                (opt) => opt.value === String(currentSelection.value),
              );
              if (!exists) {
                setLocalOptions((prev) => [...prev, currentSelection]);
              }

              // Update input value
              setInputValues([String(currentSelection.value)]);
            } else {
              setInputValues([]);
            }
          }
        }}
        onCreateOption={(inputValue) => {
          const newOption = { label: inputValue, value: inputValue };
          setLocalOptions((prev) => [...prev, newOption]);

          if (isMulti) {
            setInputValues((prev) => [...prev, inputValue]);
          } else {
            setInputValues([inputValue]);
          }
        }}
        menuPortalTarget={document.body} // Render menu in portal
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        classNames={reactSelectStyles}
      />
      {selectedOptions.map((opt) => (
        <input
          type="hidden"
          name={isMulti ? `${name}[]` : name}
          value={opt.value}
          key={opt.value}
        />
      ))}
      {isClearable && !isMulti && selectedOptions.length === 0 && (
        <input type="hidden" name={name} value="" />
      )}
    </>
  );
}

export default class extends Controller {
  static targets = ["input", "select"];
  root: any;

  safeRsolveSelectedIds() {
    const dataSelectedIds = this.element.dataset.selectedids;

    try {
      return dataSelectedIds ? JSON.parse(dataSelectedIds) : [];
    } catch (err) {
      [dataSelectedIds];
    }
  }

  connect() {
    // Clean up any existing hidden inputs before mounting
    const existingInputs = this.element.querySelectorAll(
      'input[type="hidden"]',
    );
    existingInputs.forEach((input) => input.remove());

    const dataOptions = this.element.dataset.options;
    const options = dataOptions ? JSON.parse(dataOptions) : [];
    const dataSelectedIds = this.element.dataset.selectedids;
    const selectedIds = this.safeRsolveSelectedIds();
    const labelPath = this.element.dataset.labelPath;
    const autocompleteUrl = this.element.dataset.autocompleteUrl;
    const isMulti = this.element.dataset.isMulti === "false" ? false : true;
    const creatable = this.element.dataset.creatable === "false" ? false : true;
    const isClearable = this.element.dataset.isClearable === "true";
    this.root = createRoot(this.selectTarget);
    this.root.render(
      <MultiSelectComponent
        name={this.element.dataset.name}
        options={options || []}
        selectedIds={Array.isArray(selectedIds) ? selectedIds : [selectedIds]}
        labelPath={labelPath}
        autocompleteUrl={autocompleteUrl}
        isMulti={isMulti}
        creatable={creatable}
        isClearable={isClearable}
      />,
    );
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
